import { useState, useEffect, useRef } from "react";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import { IconButton, CircularProgress } from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { toast } from "react-toastify";
import "./VoiceMicButton.css";
import { Tooltip } from 'react-tooltip';
import { useLocation } from "react-router";
import { applyPausePunctuation, COMMA_PAUSE_MS } from "../../utils/formatDictation";

const VoiceMicButton = ({ onTextExtracted, onVoiceStart, onLiveTranscript, onVoiceCancel, disabled = false, onMicButtonStateChange, setIsRecordingTwo, setIsTimerPaused, isTeacher, setIsVideoBlocked }) => {
    const [isRecording, setIsRecording] = useState(false);
    const [isConverting, setIsConverting] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);
    const isRecordingRef = useRef(false);
    // True from mic start until confirm/cancel finishes; gates live transcript updates
    const liveSessionRef = useRef(false);
    const latestTranscriptRef = useRef("");
    // Pause tracking for automatic punctuation
    const pausesRef = useRef([]); // { afterWord, gapMs }
    const lastTranscriptChangeRef = useRef({ time: 0, words: 0 });

    // Web Audio API refs for live waveform
    const canvasRef = useRef(null);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const dataArrayRef = useRef(null);
    const sourceRef = useRef(null);
    const animationRef = useRef(null);
    const streamRef = useRef(null);
    const waveformHistoryRef = useRef([]);
    const peakLevelRef = useRef(0); // Loudest level seen since the last bar was pushed
    const lastBarTimeRef = useRef(0);
    const canvasSizeRef = useRef({ width: 0, height: 0 }); // CSS pixel size of the canvas
    const location = useLocation();

    const {
        transcript,
        resetTranscript,
        browserSupportsSpeechRecognition,
    } = useSpeechRecognition();

    // Cleanup on unmount only. Depending on `listening` here tore down the
    // audio graph as soon as recognition started, freezing the waveform.
    useEffect(() => {
        return () => {
            if (isRecordingRef.current) {
                isRecordingRef.current = false;
                SpeechRecognition.stopListening();
            }
            cleanupAudioResources();
        };
    }, []);

    // Stream interim + final speech into the answer field while dictating
    useEffect(() => {
        const now = performance.now();
        const wordCount = transcript.trim() ? transcript.trim().split(/\s+/).length : 0;
        const last = lastTranscriptChangeRef.current;

        // New words after a silent gap -> record a pause after the previous last word
        if (liveSessionRef.current && last.words > 0 && wordCount > last.words) {
            const gapMs = now - last.time;
            if (gapMs >= COMMA_PAUSE_MS) {
                pausesRef.current.push({ afterWord: last.words, gapMs });
            }
        }
        lastTranscriptChangeRef.current = { time: now, words: wordCount };
        latestTranscriptRef.current = transcript;

        if (liveSessionRef.current && onLiveTranscript) {
            onLiveTranscript(punctuate(transcript));
        }
    }, [transcript]);

    // Punctuation is best-effort; never let it block the text from showing
    const punctuate = (text, options) => {
        try {
            return applyPausePunctuation(text, pausesRef.current, options);
        } catch (err) {
            console.error("Dictation punctuation failed, using raw transcript:", err);
            return (text || "").trim();
        }
    };

    const cleanupAudioResources = () => {
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
            animationRef.current = null;
        }
        if (sourceRef.current) {
            sourceRef.current.disconnect();
            sourceRef.current = null;
        }
        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        waveformHistoryRef.current = [];
        peakLevelRef.current = 0;
        lastBarTimeRef.current = 0;
    };

    // Waveform tuning
    const BAR_WIDTH = 2;
    const BAR_GAP = 3;
    const BAR_SPACING = BAR_WIDTH + BAR_GAP;
    const BAR_INTERVAL_MS = 60; // How often a new bar enters from the right
    const MIN_BAR_HEIGHT = 3;
    const NOISE_FLOOR = 0.01; // RMS below this is treated as silence
    const LEVEL_GAIN = 6; // Scales typical speech RMS (~0.03-0.2) into 0..1

    // Current mic loudness (0..1) from the RMS of the time-domain signal
    const getMicLevel = () => {
        const analyser = analyserRef.current;
        const data = dataArrayRef.current;
        analyser.getByteTimeDomainData(data);

        let sumSquares = 0;
        for (let i = 0; i < data.length; i++) {
            const sample = (data[i] - 128) / 128;
            sumSquares += sample * sample;
        }
        const rms = Math.sqrt(sumSquares / data.length);
        const gated = Math.max(0, rms - NOISE_FLOOR);
        // sqrt curve keeps softer speech visible without clipping loud speech
        return Math.min(1, Math.sqrt(gated * LEVEL_GAIN));
    };

    const drawWaveform = (timestamp = performance.now()) => {
        if (!isRecordingRef.current) return;

        const canvas = canvasRef.current;
        if (!canvas || !analyserRef.current || !dataArrayRef.current) return;

        const ctx = canvas.getContext('2d');
        const { width, height } = canvasSizeRef.current;
        const history = waveformHistoryRef.current;

        // Sample every frame but keep the peak, so short syllables aren't missed
        peakLevelRef.current = Math.max(peakLevelRef.current, getMicLevel());

        if (timestamp - lastBarTimeRef.current >= BAR_INTERVAL_MS) {
            history.push(peakLevelRef.current);
            peakLevelRef.current = 0;
            lastBarTimeRef.current = timestamp;

            const maxBars = Math.floor(width / BAR_SPACING);
            while (history.length > maxBars) history.shift();
        }

        ctx.clearRect(0, 0, width, height);
        ctx.fillStyle = '#000000';

        // Newest bar on the right; older bars scroll left
        const totalBars = history.length;
        for (let i = 0; i < totalBars; i++) {
            const x = width - (totalBars - i) * BAR_SPACING;
            const barHeight = MIN_BAR_HEIGHT + history[i] * (height - MIN_BAR_HEIGHT);
            const y = (height - barHeight) / 2;

            ctx.beginPath();
            if (ctx.roundRect) {
                ctx.roundRect(x, y, BAR_WIDTH, barHeight, BAR_WIDTH / 2);
            } else {
                ctx.rect(x, y, BAR_WIDTH, barHeight);
            }
            ctx.fill();
        }

        animationRef.current = requestAnimationFrame(drawWaveform);
    };

    const setupAudioVisualization = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true
                }
            });
            // User cancelled while the mic permission prompt was open
            if (!isRecordingRef.current) {
                stream.getTracks().forEach(track => track.stop());
                return;
            }
            streamRef.current = stream;

            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            // A suspended context returns flat data, which renders a frozen waveform
            if (audioContext.state === 'suspended') {
                await audioContext.resume();
            }

            const analyser = audioContext.createAnalyser();
            const source = audioContext.createMediaStreamSource(stream);

            analyser.fftSize = 1024;
            analyser.smoothingTimeConstant = 0.3;

            // Time-domain data needs fftSize samples
            const dataArray = new Uint8Array(analyser.fftSize);

            source.connect(analyser);

            audioContextRef.current = audioContext;
            analyserRef.current = analyser;
            dataArrayRef.current = dataArray;
            sourceRef.current = source;

            // Size the backing store for the device pixel ratio so bars stay crisp
            const canvas = canvasRef.current;
            if (canvas) {
                const rect = canvas.getBoundingClientRect();
                const width = rect.width || 300;
                const height = rect.height || 33;
                const dpr = window.devicePixelRatio || 1;
                canvas.width = width * dpr;
                canvas.height = height * dpr;
                canvas.getContext('2d').setTransform(dpr, 0, 0, dpr, 0, 0);
                canvasSizeRef.current = { width, height };
            }

            // Start with a flat line across the full width so movement is visible immediately
            const maxBars = Math.floor(canvasSizeRef.current.width / BAR_SPACING);
            waveformHistoryRef.current = new Array(maxBars).fill(0);
            peakLevelRef.current = 0;
            lastBarTimeRef.current = 0;

            // Single animation loop
            if (animationRef.current) cancelAnimationFrame(animationRef.current);
            animationRef.current = requestAnimationFrame(drawWaveform);
        } catch (err) {
            console.error('Error setting up audio visualization:', err);
            toast.error('Failed to access microphone for visualization');
        }
    };
    const language = location?.state?.assignment?.language || "en"; // Get language from assignment object

    const startRecording = async () => {
        if (disabled || !browserSupportsSpeechRecognition) return;

        resetTranscript();
        latestTranscriptRef.current = "";
        pausesRef.current = [];
        lastTranscriptChangeRef.current = { time: 0, words: 0 };
        onVoiceStart?.();
        liveSessionRef.current = true;
        setIsConverting(true);
        isRecordingRef.current = true;
        setIsRecording(true);
        setIsRecordingTwo(true);
        onMicButtonStateChange(true);
        setIsVideoBlocked(true)
        // Setup audio visualization
        await setupAudioVisualization();

        SpeechRecognition.startListening({
            continuous: true,
            language: language,
        });
    };


    const stopRecording = () => {
        isRecordingRef.current = false;
        setIsRecording(false);
        setIsRecordingTwo(false);
        setIsConverting(false);
        onMicButtonStateChange(false);

        SpeechRecognition.stopListening();
        cleanupAudioResources();
    };


    const handleCancel = () => {
        // Close the session before resetTranscript so the reset isn't streamed as an edit
        liveSessionRef.current = false;
        onVoiceCancel?.();
        stopRecording();
        resetTranscript();
        setIsConfirming(false);
        setIsVideoBlocked(false)
    };

    const handleConfirm = async () => {
        // Show loader immediately
        setIsConfirming(true);
        setIsTimerPaused(false)

        // Stop the recognition but keep UI visible
        isRecordingRef.current = false;
        setIsRecording(false);
        setTimeout(() => {
            setIsRecordingTwo(false);
        }, 2000);
        SpeechRecognition.stopListening();
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Read after the wait so words finalized during stopListening aren't dropped.
        // Always commit (even empty) so the parent can settle the live text.
        liveSessionRef.current = false;
        onTextExtracted(punctuate(latestTranscriptRef.current, { final: true }));

        // Reset everything NOW (after 7 seconds)
        setIsRecording(false);

        setIsConverting(false);
        resetTranscript();
        setIsConfirming(false);
        onMicButtonStateChange(false);
        cleanupAudioResources();
        setIsTimerPaused(true)
        setIsVideoBlocked(false)
    };

    return (
        <div className="voice-mic-container">
            {!isRecording && !isConfirming ? (
                <>
                    <IconButton
                        onClick={startRecording}
                        disabled={disabled || !browserSupportsSpeechRecognition || isTeacher}
                        data-tooltip-id="speech-audio-tooltip"
                        className="mic-button"
                        sx={{
                            backgroundColor: "#f0f4ff",
                            color: "#0068ff",
                            "&:hover": {
                                backgroundColor: "#e0ebff",
                            },
                            width: 48,
                            height: 48,
                            opacity: disabled ? 0.5 : 1,
                            cursor: disabled ? "not-allowed" : "pointer",
                        }}
                    >
                        <MicIcon className="mic-img" />
                    </IconButton>
                    <Tooltip
                        id="speech-audio-tooltip"
                        place="top"
                        content={"Dictate"}
                        type="light"
                        effect="float"
                    />
                </>
            ) : (
                <div className="recording-container">
                    <div className="waveform-wrapper">
                        {/* <canvas
                            ref={canvasRef}
                            style={{
                                width: '300px',
                                height: '33px',
                                display: 'block',
                                opacity: isConfirming ? 0.5 : 1,
                                transition: 'opacity 0.3s ease',
                                backgroundColor: 'transparent'
                            }}
                        /> */}
                        <canvas
    ref={canvasRef}
    style={{
        width: '100%',
        maxWidth: '300px',
        height: '33px',
        display: 'block',
        opacity: isConfirming ? 0.5 : 1,
        transition: 'opacity 0.3s ease',
        backgroundColor: 'transparent'
    }}
/>
                    </div>

                    <div className="recording-controls">
                        <IconButton
                            onClick={handleCancel}
                            className="cancel-button"
                            disabled={isConfirming}
                            sx={{
                                color: "#000000",
                                "&:hover": {
                                    backgroundColor: "#ffebeb",
                                },
                                opacity: isConfirming ? 0.5 : 1,
                            }}
                        >
                            <CloseIcon />
                        </IconButton>

                        <IconButton
                            onClick={handleConfirm}
                            className="confirm-button"
                            disabled={isConfirming}
                            sx={{
                                color: "#000000",
                                "&:hover": {
                                    backgroundColor: "#e8f5e9",
                                },
                            }}
                        >
                            {isConfirming ? (
                                <CircularProgress size={14} sx={{ color: "#000000" }} />
                            ) : (
                                <CheckIcon />
                            )}
                        </IconButton>
                    </div>
                </div>
            )}

            {/* {isRecording && transcript && !isConfirming && (
                <div className="transcript-preview">
                    <p>{transcript}</p>
                </div>
            )} */}
        </div>
    );
};

export default VoiceMicButton;